// i18n.js - Internationalization Engine

// Detect user language or fallback to saved
const savedLang = localStorage.getItem('ai_strategy_hub_lang');
const browserLang = navigator.language.startsWith('de') ? 'de' : 'en';
let currentLang = savedLang || browserLang;

const dict = {
  en: {
    nav: {
      dashboard: "Dashboard",
      explorer: "Framework Explorer",
      assessment: "Assessment",
      analyzer: "Document Analyzer",
      frameworkBuilder: "Framework Builder",
      research: "Research Agent",
      metaStrategy: "Meta Strategy",
      euAiAct: "EU AI Act Engine",
      roadmap: "Roadmap",
      advisor: "AI Strategy Advisor",
      simulator: "Gap Simulator",
      sources: "Sources & Attribution",
      dependencies: "Dependency Map",
      evolution: "Evolution Agent",
      playbook: "AI Playbook",
      backendOffline: "Backend Offline",
      backendOnline: "Backend Online"
    },
    dimensions: {
      strategy: "Strategy & Leadership",
      data: "Data & Infrastructure",
      governance: "Governance & Compliance",
      technology: "Technology & MLOps",
      talent: "Talent & Culture",
      ethics: "Ethics & Responsible AI",
      processes: "Processes & Scaling"
    },
    levels: {
      exploring: "Exploring",
      experimenting: "Experimenting",
      operationalizing: "Operationalizing",
      scaling: "Scaling",
      transforming: "Transforming"
    },
    dashboard: {
      title: "Dashboard",
      desc: "Your AI Maturity at a glance — powered by insights synthesized from NIST, EU AI Act, Google, Microsoft, OWASP, and UNESCO frameworks.",
      statScore: "Maturity Score",
      statLevel: "Maturity Level",
      statAnalyses: "Analyses",
      statSources: "Research Sources",
      radarTitle: "📊 Maturity Radar",
      breakdownTitle: "🎯 Score Breakdown",
      emptyStateLine1: "📋",
      emptyStateLine2: "Complete an assessment or analyze a document to see your scores",
      startAssessment: "Start Assessment",
      quickActions: "⚡ Quick Actions",
      actionExplore: "Explore Frameworks",
      actionAssess: "Start Assessment",
      actionAnalyze: "Analyze Document",
      actionBuilder: "Framework Builder",
      actionMeta: "Meta Strategy",
      actionRoadmap: "View Roadmap",
      trendTitle: "📈 Score Trend",
      emptyTrend: "Complete at least 2 assessments to see your trend.",
      historyTitle: "🕒 Assessment History & Reports",
      btnReport: "📄 Generate Consulting Report (PDF)",
      btnNewAssessment: "✅ New Assessment",
      emptyHistory: "No assessments yet. Start with the assessment or upload a document.",
      deltaPlus: "📈 +{delta} points since last",
      deltaMinus: "📉 {delta} points since last",
      deltaSame: "→ No change",
      onboardingTitle: "👋 Welcome to AI Strategy Hub",
      onboardingDesc1: "This is a comprehensive AI Maturity Assessment platform built on 6 global frameworks.",
      onboardingDesc2: "You can start small or dive deep right away. Here are the best ways to begin:",
      onboardingOpt1Title: "1. Quick Assessment (Manual)",
      onboardingOpt1Desc: "Answer the checklist questions yourself to get a baseline score across 7 dimensions.",
      onboardingOpt1Btn: "Start Assessment",
      onboardingOpt2Title: "2. Document Analysis (Automated)",
      onboardingOpt2Desc: "Already have an AI strategy document? Upload it and let the AI score it against the framework.",
      onboardingOpt2Btn: "Go to Document Analyzer",
      onboardingOpt3Title: "3. Chat with the Advisor (Interactive)",
      onboardingOpt3Desc: "Ask the AI Strategy Advisor about the frameworks, best practices, or what to do next.",
      onboardingOpt3Btn: "Chat with Advisor",
      onboardingClose: "Get Started",
      dimensionScore: "Dimension Score"
    },
    assessment: {
      title: "Maturity Assessment",
      desc: "Assess your organization's AI readiness across 7 dimensions. Each checkpoint is traced to its original framework source.",
      btnReset: "↺ Reset",
      btnSubmit: "Submit Assessment",
      resultTitle: "🎯 Assessment Result",
      overallProgress: "Overall Progress",
      loading: "Loading assessment…",
      aiSuggested: "🤖 {count} checkpoints pre-filled from Document Analyzer (\"{source}\")",
      showEvidence: "Evidence Chain",
      hideEvidence: "Hide Evidence",
      noEvidence: "No evidence extracted from document.",
      submitSuccess: "Assessment saved! Score: {score}",
      submitError: "Could not save assessment. Is the backend running?",
      submitEmpty: "Please check at least one item before submitting",
      resetConfirm: "Are you sure you want to reset your progress?",
      resetSuccess: "Assessment reset",
      overallScore: "Overall Score",
      viewRoadmap: "🗺️ View Roadmap →",
      strategicGuidance: "🧭 Strategic Guidance",
      covered: "✅ Covered",
      notCovered: "❌ Not covered",
      confidence: "Confidence: {pct}%",
      evidenceTitle: "Evidence:",
      recommendationTitle: "💡 Recommendation:",
      sourceChunksTitle: "SOURCE CHUNKS:"
    },
    explorer: {
      title: "Framework Explorer",
      desc: "Browse the synthesized AI maturity matrix — every recommendation traced to its original source from NIST, EU AI Act, Google, Microsoft, OWASP, and UNESCO.",
      filterTitle: "🔎 Filter by Source Framework",
      loading: "Loading meta-model…",
      error: "Could not load the maturity model.",
      level: "Level",
      viewSourceDocs: "📄 View Source Documents",
      allSources: "All Sources",
      checkpointsAcross: "{total} checkpoints across {dims} dimensions",
      matchFilter: "{visible} of {total} checkpoints match \"{filter}\"",
      evidenceRef: "Evidence & References",
      openSource: "Open Source →",
      deepDive: "🤖 Deep Dive",
      deepDiveLoading: "⏳ Analyzing..."
    },
    analyzer: {
      title: "Document Analyzer",
      desc: "Upload your AI strategy documents — the RAG pipeline checks them against all checkpoints of the maturity model using Gemini.",
      uploadTitle: "📤 Upload Document",
      dropzoneText: "Drag & drop your document here",
      dropzoneHint: "Supports PDF, DOCX, TXT, MD (max 20 MB)",
      orImport: "OR IMPORT FROM RESEARCH",
      importLabel: "Analyze an article found by the Research Agent",
      importLoading: "Loading sources...",
      btnImport: "Import & Analyze",
      btnAnalyze: "🔍 Analyze",
      statusAnalyzing: "Analyzing document…",
      progressSteps: {
        extracting_text: '📄 Extracting text from document…',
        chunking: '✂️ Splitting into semantic chunks…',
        embedding: '🧠 Generating embeddings…',
        evaluating: '🔍 Evaluating checkpoints…',
        scoring: '📊 Calculating maturity scores…',
        generating_summary: '✨ Generating executive summary…',
        storing_results: '💾 Saving results…',
        completed: '✅ Analysis complete!'
      },
      resultTitle: "📊 Analysis Result",
      maturityScore: "Maturity Score",
      reportTitle: "📋 Detailed Report",
      strengths: "✅ Strengths",
      gaps: "⚠️ Gaps",
      recommendations: "💡 Recommendations",
      expandModelTitle: "🏗️ Expand your Meta-Model",
      expandModelDesc: "Imported a research document? Use the Framework Builder to extract novel checkpoints and grow your knowledge base.",
      expandModelBtn: "Go to Framework Builder →",
      uploadError: "Upload failed: {msg}",
      analysisError: "Analysis failed: {msg}",
      analysisComplete: "Analysis complete! Score: {score}",
      pastAnalyses: "📁 Past Analyses",
      noSources: "No research sources available yet",
      chooseSource: "-- Choose a source --",
      couldNotLoad: "Could not load sources",
      fileTooLarge: "File too large (max 20 MB)",
      uploading: "Uploading…",
      docUploaded: "Document uploaded: {name}",
      analyzingBtn: "Analyzing…",
      btnAnalyze: "🔍 Analyze",
      analysisFailedLog: "Analysis failed. Check the backend logs.",
      aiExecSummary: "🤖 AI Executive Summary",
      noStrengths: "No strengths identified",
      noGaps: "No gaps identified",
      noRecommendations: "No recommendations",
      cpMapping: "🗺️ Checkpoint Mapping",
      cpEvaluated: "{count} checkpoints evaluated",
      coveredCount: "Covered",
      gapsCount: "Gaps",
      coveragePct: "Coverage",
      dimsCount: "Dimensions",
      filterAll: "All",
      filterCovered: "✅ Covered",
      filterGaps: "❌ Gaps",
      levelX: "Level {level}",
      confidenceLabel: "Confidence",
      depthLabels: {
        1: "Mentioned",
        2: "Detailed",
        3: "Implemented"
      },
      noCheckpoints: "No checkpoints match this filter.",
      noAnalyses: "No analyses yet. Upload a document to get started.",
      reportNotAvailable: "Report not available yet",
      startBackendAnalyses: "Start the backend to view past analyses"
    },
    research: {
      title: "Research Agent",
      desc: "Discover new AI strategy frameworks, regulations, and best practices — automatically evaluated for relevance to your maturity model.",
      runResearch: "🔬 Run Research",
      ingestTitle: "📥 Source Ingestion — Analyze & Integrate New Sources",
      ingestDesc: "Feed a URL or upload a document (PDF, TXT, DOCX). Gemini extracts key arguments and maps them to the 7 maturity dimensions. You can then selectively integrate them into your model.",
      analyzeUrl: "Analyze URL",
      uploadFile: "Upload File",
      analyzeBtn: "Analyze",
      dragDropText: "Drag & drop or click to browse",
      dragDropHint: "PDF, TXT, DOCX, MD",
      apiKeysMissing: "API Keys not configured",
      apiKeysDesc: "The Research Agent requires API keys. Create a <code>.env</code> file in the project root:",
      apiKeysRestart: "Then restart the backend.",
      searchConfig: "🔎 Research Configuration",
      customQuery: "Custom Search Query (optional)",
      customQueryPlaceholder: "e.g., AI governance best practices 2026 enterprise",
      focusDims: "Focus Dimensions",
      maxSources: "Max sources to fetch:",
      statusHelp: "💡 Select dimensions or enter a query, then click \"Run Research\"",
      pdfOnly: "Only search for PDFs",
      langAll: "All Languages",
      langEn: "English",
      langDe: "Deutsch",
      resultsCount: "{count} results",
      lastResults: "📊 Last Search Results",
      sourcesFeed: "📚 Sources Feed",
      catAll: "All Categories",
      catFramework: "Frameworks",
      catRegulation: "Regulations",
      catWhitepaper: "Whitepapers",
      catArticle: "Articles",
      catReport: "Reports",
      catTool: "Tools",
      dimAll: "All Dimensions",
      searchingBtn: "⏳ Searching…",
      searchingStatus: "🔄 Research agent is searching and evaluating sources…",
      researchFailed: "Research failed",
      sourcesStored: "✅ {stored} new sources stored ({found} found, {skipped} skipped)",
      noNewSources: "ℹ️ {found} found, but no new sources to store",
      statFound: "Found",
      statNew: "New",
      statStored: "Stored",
      statSkipped: "Low Relevance",
      sourceCountLabel: "{total} sources · {new} new",
      noSourcesYet: "No sources yet. Run a research search to discover new AI strategy content.",
      markedAsRead: "Marked as read",
      couldNotUpdate: "Could not update",
      startBackendSources: "Start the backend to view research sources",
      noSummary: "No summary available",
      relevantPct: "📊 {pct}% relevant",
      readBtn: "✓ Read",
      extractBtn: "🏗️ Extract",
      extractTooltip: "Extract novel checkpoints for the Framework Builder",
      extractingBtn: "⏳ ...",
      extractingMsg: "Extracting novel checkpoints...",
      enterUrl: "Please enter a URL",
      fetchingStatus: "🔄 Fetching and analyzing source with Gemini...",
      extractedArgs: "✅ Extracted {count} arguments from \"{source}\"",
      analysisFailed: "Analysis failed: {msg}",
      selectFileFirst: "Please select a file first",
      analyzingFile: "🔄 Analyzing \"{file}\" with Gemini...",
      noArgsExtracted: "No arguments extracted",
      extractedArgsTitle: "📋 Extracted Arguments — {source}",
      integrateAllBtn: "⚡ Integrate All Selected",
      noArgsSelected: "No arguments selected",
      integratingBtn: "⏳ Integrating...",
      integrationSuccess: "✅ {count} arguments integrated → {target}",
      integrationStatus: "✅ {integrated} of {total} integrated into:",
      viewInExplorer: "View in Explorer →",
      integrationFailed: "Integration failed: {msg}",
      noNovelInsights: "No novel insights found — the Meta-Model already covers this source.",
      novelCpProposals: "🏗️ {count} Novel Checkpoint Proposals",
      integrateSelected: "➕ Integrate Selected ({count})",
      integrating: "Integrating...",
      cpsIntegratedSuccess: "{count} checkpoints integrated into the Meta-Model!",
      cpsIntegratedStatus: "✅ {count} checkpoints successfully integrated!"
    },
    advisor: {
      title: "🧠 AI Strategy Advisor",
      desc: "Your personal AI strategy consultant — grounded in your assessment data, framework knowledge, and research sources.",
      loadingContext: "Loading context...",
      welcomeMsg: "<strong>Welcome!</strong> I'm your AI Strategy Advisor, powered by Gemini Pro and grounded in the full maturity framework.<br><br>I know your current assessment scores, gaps, strengths, and research sources. Ask me anything about your AI maturity strategy.",
      sugg1: "🎯 What should I focus on next?",
      sugg1q: "What should I focus on next to improve my AI maturity?",
      sugg2: "⚖️ EU AI Act readiness?",
      sugg2q: "How does our organization compare to EU AI Act requirements?",
      sugg3: "📋 90-day action plan",
      sugg3q: "Give me a 90-day action plan to move from our current level to the next maturity level.",
      sugg4: "⚡ Quick wins this week?",
      sugg4q: "What are the quick wins we can achieve this week with minimal effort?",
      inputPlaceholder: "Ask about your AI strategy, maturity gaps, compliance, or next steps...",
      sendBtn: "Send ↑",
      contextDims: "📊 {dims} dimensions, {cps} checkpoints",
      contextAssLoaded: "✅ Assessment loaded",
      contextResLoaded: "🔬 Research loaded",
      backendOffline: "⚠️ Backend offline",
      sending: "...",
      failedResponse: "Failed to get response: {msg}",
      advisorError: "Advisor error: {msg}",
      unknownError: "Unknown error",
      youAvatar: "You"
    },
    roadmap: {
      title: "Actionable Roadmap",
      desc: "See what's needed to reach your next maturity level — prioritized by impact and effort.",
      exportMd: "📄 Export MD",
      exportPdf: "📕 Export PDF",
      targetConfig: "🎯 Target Configuration",
      targetLevel: "Target Maturity Level",
      generateBtn: "Generate Roadmap",
      generatingBtn: "⏳ Generating…",
      currentScore: "Current Score",
      currentLevel: "Current Level",
      openGaps: "Open Gaps",
      quickWins: "Quick Wins",
      qwTitle: "⚡ Quick Wins — Low effort, high impact",
      gapsTitle: "📊 Gaps by Dimension",
      fullRoadmapTitle: "📋 Full Roadmap",
      roadmapItemsCount: "{count} items · Target: Level {level} {levelName}",
      emptyStateMsg: "Select a target level and click \"Generate Roadmap\" to see your prioritized action items.",
      effortLow: "Low",
      effortMed: "Medium",
      effortHigh: "High",
      effortSuffix: "{effort} effort",
      levelPlus: "Level {level}+",
      qwBadge: "⚡ Quick Win",
      roadmapGenerated: "Roadmap generated: {count} gaps found",
      roadmapFailed: "Could not generate roadmap. Is the backend running?",
      exportSuccess: "{format} report downloaded!",
      exportFailed: "Export failed: {msg}"
    },
    simulator: {
      title: "🎮 Gap Simulator",
      desc: "Toggle checkpoints to simulate score changes — discover which actions have the highest impact on your maturity level.",
      resetBtn: "↩️ Reset",
      currentState: "📊 Current State",
      simState: "🎯 Simulated State",
      impactTitle: "⚡ Highest Impact Checkpoints",
      impactSub: "Sorted by score impact — toggle to simulate",
      noChange: "No change",
      ptsIncrease: "📈 +{pts} points",
      ptsDecrease: "📉 {pts} points",
      allFulfilled: "🎉 All checkpoints fulfilled!",
      dimImpactTitle: "📊 Dimension Impact",
      failedLoad: "Could not load framework model."
    },
    meta: {
      title: "Meta Strategy",
      desc: "Strategic synthesis of all 6 global AI frameworks — actionable Do's & Don'ts, framework-specific insights, and maturity-aware recommendations for every dimension.",
      overviewTitle: "🧭 Strategic Overview",
      generateAiRec: "🤖 Generate AI Recommendations",
      metaSummary: "This meta-model synthesizes <strong>6 frameworks</strong> (NIST, EU AI Act, Google, Microsoft, OWASP, UNESCO) into <strong>7 dimensions</strong> with <strong>69 checkpoints</strong>. This page distills the strategic essence — what the world's leading frameworks agree on, where they differ, and what you should (and should not) do at each maturity level.",
      globalFws: "Global Frameworks",
      dimensions: "Dimensions",
      checkpoints: "Checkpoints",
      aiRecTitle: "🤖 Personalized AI Recommendations",
      loadingGuidance: "Loading strategic guidance...",
      checkpointsLabel: "Checkpoints",
      weightLabel: "Weight:",
      dosTitle: "✅ Do's",
      recsCount: "{count} recommendations",
      achieved: "✓ achieved",
      deepDiveBtn: "🤖 Deep Dive",
      dontsTitle: "❌ Don'ts",
      warningsCount: "{count} warnings",
      whatFrameworksSay: "🔍 What the Frameworks Say",
      analyzing: "⏳ Analyzing...",
      failedDeepDive: "Failed to generate deep dive: {msg}",
      modelNotLoaded: "Model not loaded yet",
      assessmentRequired: "Complete the Assessment first to get personalized recommendations",
      generatingBtn: "⏳ Generating...",
      geminiAnalyzing: "Gemini is analyzing your maturity profile...",
      generationFailed: "Could not generate recommendations: {msg}",
      noRecommendations: "No recommendations generated",
      executiveSummary: "Executive Summary",
      expectedImpact: "📈 Expected impact:"
    },
    report: {
      noAssessment: "No assessment data available. Please complete the assessment first.",
      goToAssessment: "Go to Assessment",
      generatingAi: "Generating Executive Summary via AI (Gemini 3.1 Pro)...",
      checkpointsUnmet: "{dim}: {unmet} of {total} checkpoints unmet",
      checkpointsMet: "{dim}: {met} of {total} checkpoints met ({score}%)",
      briefingFailed: "Briefing generation failed",
      headerSub: "AI Strategy Hub — Executive Report",
      headerTitle: "AI Maturity Executive Briefing",
      generatedOn: "Generated on {date}",
      scoreLabel: "Score / 100 · Level {level}",
      footer: "AI Strategy Hub • Built on NIST, EU AI Act & 4 more • Gemini 3.1 Pro",
      downloadPdf: "📥 Download PDF",
      generatingPdf: "⏳ Generating PDF...",
      printBtn: "🖨️ Print",
      backBtn: "🔙 Back",
      failedLoad: "Failed to load report: {msg}",
      pdfFailed: "PDF generation failed: {msg}"
    },
    eu: {
      title: "⚖️ EU AI Act Compliance Engine",
      desc: "Automated auditing of your AI portfolio against the European Artificial Intelligence Act (enforcement starting August 2026).",
      initializing: "Initializing Compliance Audit...",
      failedLoad: "Could not load framework model. Backend might be offline.",
      criticalRisk: "Critical Risk",
      lowRisk: "Low Risk",
      mediumRisk: "Medium Risk",
      prioritizedGaps: "🚨 Prioritized Gaps ({count})",
      generalObligations: "General Obligations",
      violation: "Violation:",
      fixViaAi: "🤖 Fix via AI Deep Dive",
      deepDiveContext: "EU AI Act Compliance gap mapping to: {refs}",
      fullyCompliantTitle: "Fully Compliant",
      fullyCompliantDesc: "You have fulfilled all mapped checkpoints linked to the EU AI Act.",
      readinessScore: "Readiness Score",
      regExposure: "Regulatory Exposure",
      openGaps: "Open Compliance Gaps",
      penaltiesTitle: "⚠️ Penalties for Non-Compliance",
      penaltiesDesc: "Under the EU AI Act, administrative fines can reach up to <strong>€35,000,000</strong> or <strong>7% of total worldwide annual turnover</strong> for the preceding financial year, whichever is higher, for non-compliance with prohibited AI practices. For obligations related to high-risk AI systems, fines can reach up to <strong>€15,000,000</strong> or <strong>3%</strong> of global turnover.",
      completeAssessmentPrompt: "Complete the Assessment to analyze your specific compliance gaps.",
      startAssessmentBtn: "Start Assessment",
      genRoadmapTitle: "Generate Compliance Roadmap",
      genRoadmapDesc: "Turn your {count} compliance gaps into a prioritized, actionable remediation plan with effort estimates and quick wins.",
      openRoadmapBtn: "🗺️ Open Roadmap →",
      generatingFix: "⏳ Generating Fix...",
      failedDeepDive: "Failed to generate deep dive: {msg}"
    },
    builder: {
      title: "🏗️ Framework Builder",
      desc: "Enrich the AI Strategy Hub Meta-Model by extracting novel strategic guidelines from research documents. Auto-enriched with linked research evidence.",
      coverageTitle: "📊 Research Coverage Analysis",
      coverageDesc: "Shows which maturity dimensions have the most/least research backing. Focus your research on under-covered areas.",
      step1Title: "1. Select Research Source",
      step1Desc: "Choose a document that was previously imported via the Research Agent (.txt)",
      loadingSources: "Loading sources...",
      extractBtn: "Extract Novel Insights",
      extractingMsg: "Analyzing framework and distilling strategic rules... (This may take up to 20 seconds)",
      step2Title: "2. Review & Integrate",
      integrateBtn: "➕ Integrate Selected ({count})",
      step2Desc: "The AI has identified the following strategic checkpoints that are currently missing from your Meta-Model. Select the ones you want to officially append to your framework.",
      activityTitle: "📜 Integration Activity Log",
      noCoverageData: "No coverage data available",
      checkpointsStat: "Checkpoints",
      sourcesStat: "Research Sources",
      builderAdditionsStat: "Builder Additions",
      checkpointsLabel: "{count} checkpoints",
      sourcesLabel: "{count} sources",
      builtLabel: "+{count} built",
      researchBtn: "🔬 Research",
      failedLoadCoverage: "Could not load coverage data. Start the backend first.",
      noActivity: "No activity yet. Run research or integrate checkpoints to see the lifecycle here.",
      actionResearch: "Research Completed",
      actionExtracted: "Extracted for Framework",
      actionIntegrated: "Checkpoint Integrated",
      sourcesStored: "{count} sources stored",
      proposalsCount: "({count} proposals)",
      failedLoadActivity: "Could not load activity data.",
      noSourcesMsg: "No imported research found. Go to Document Analyzer and import one first.",
      chooseSource: "-- Choose a research document --",
      failedLoadSources: "Could not load sources",
      extractionFailed: "Extraction failed: {msg}",
      noNovelInsights: "No novel insights found. The Meta-Model already covers this document comprehensively.",
      linkedEvidence: "📚 Linked Research Evidence",
      targetDimension: "Target Dimension:",
      rationale: "Rationale:",
      minLevel: "Min Level:",
      integrating: "Integrating...",
      integrationSuccess: "Successfully added {count} checkpoints to the Meta-Model!",
      integrationFailed: "Integration failed: {msg}"
    },
    sources: {
      title: "📚 Sources & Attribution",
      desc: "Every checkpoint in this platform is traceable to its original framework. This page provides full transparency on the 6 globally recognized standards that form the foundation of the maturity model.",
      coverageTitle: "🔬 Framework Coverage",
      checkpointsTotal: "{count} checkpoints total",
      checkpointsPct: "{count} checkpoints ({pct}%)",
      version: "📄 <strong>Version:</strong> {version}",
      published: "📅 <strong>Published:</strong> {date}",
      officialSource: "🔗 Official Source ↗",
      disclaimerTitle: "⚖️ Disclaimer & Fair Use",
      disclaimer1: "AI Strategy Hub synthesizes publicly available frameworks and guidelines into a unified assessment model. All referenced frameworks remain the intellectual property of their respective organizations. This tool does <strong>not</strong> replace professional legal, compliance, or audit advice.",
      disclaimer2: "<strong>For EU AI Act compliance:</strong> Consult qualified legal counsel for binding regulatory assessments. The compliance readiness scores in this tool are indicative and based on self-assessment — they are not legally binding conformity assessments.",
      disclaimer3: "Framework data was last verified in <strong>April 2026</strong>. Source frameworks may have been updated since. Always refer to the official sources linked above for the latest versions."
    },
    evolution: {
      title: "Evolution Dashboard",
      subtitle: "Autonomous framework evolution — monitor, approve, and manage how your AI maturity model grows and refines itself over time.",
      status_loading: "Loading status…",
      status_active: "Evolution Agent Active",
      status_paused: "Evolution Agent Paused",
      status_running: "Evolution Running…",
      status_offline: "Agent Offline",
      next_run: "Next scheduled run",
      last_run: "Last run",
      run_now: "Run Now",
      configure: "Configure",
      running: "Running…",
      total_runs: "Total Runs",
      integrated: "Checkpoints Integrated",
      redundancies_resolved: "Redundancies Resolved",
      avg_quality: "Avg Quality Score",
      timeline_title: "Evolution Timeline",
      timeline_empty: "No evolution runs yet. Trigger your first run to get started.",
      growth_title: "Framework Growth",
      no_growth_data: "Not enough data for growth chart yet.",
      proposals_title: "Pending Proposals",
      proposals_empty: "No pending proposals. All caught up!",
      approve: "Approve",
      reject: "Reject",
      approve_all: "Approve All",
      quality: "Quality",
      impact: "Impact",
      novelty: "Novelty",
      proposal_approved: "Proposal approved!",
      proposal_rejected: "Proposal rejected.",
      bulk_approved: "All proposals approved!",
      redundancy_title: "Redundancy Scanner",
      scan_redundancies: "Scan for Redundancies",
      scanning: "Scanning…",
      redundancy_empty: "Click \"Scan\" to detect similar or overlapping checkpoints.",
      no_redundancies: "No redundancies found — your framework is clean!",
      similarity: "similar",
      merge: "Merge",
      merge_success: "Checkpoints merged successfully!",
      snapshots_title: "Snapshots & Rollback",
      snapshots_empty: "No snapshots available yet.",
      date: "Date",
      rollback: "Rollback",
      rollback_confirm: "Are you sure you want to rollback to {version}? This will revert the framework to that state.",
      rollback_success: "Successfully rolled back to {version}!",
      trigger_success: "Evolution run triggered successfully! This takes 30-60 minutes.",
      trigger_error: "Failed to trigger evolution",
      already_running: "Evolution cycle is already running — check back in a few minutes.",
      run_complete: "Evolution cycle completed! Refreshing data...",
      backend_offline: "Start the backend to view evolution data.",
      no_logs: "No log entries available.",
      imminent: "Imminent",
      config_schedule: "Schedule",
      config_auto_approve: "Auto-Approve",
      config_auto_approve_label: "Automatically approve high-quality proposals",
      config_quality_threshold: "Quality Threshold (%)",
      config_save: "Save Configuration",
      config_saved: "Configuration saved!"
    },
    playbook_title: "AI Strategy Playbook",
    playbook_subtitle: "Your step-by-step guide from zero to AI-first organization",
    playbook_based_on: "Based on {count} checkpoints · Model {version}",
    playbook_phase: "Phase",
    playbook_steps: "Steps",
    playbook_duration: "Est. Duration",
    playbook_key_focus: "Key Focus",
    playbook_effort_low: "Low Effort",
    playbook_effort_medium: "Medium Effort",
    playbook_effort_high: "High Effort",
    playbook_priority_critical: "Critical",
    playbook_priority_high: "High Priority",
    playbook_priority_medium: "Medium",
    playbook_priority_standard: "Standard",
    playbook_progress: "Progress",
    playbook_completed: "{count} of {total} completed",
    playbook_all_completed: "Phase completed! 🎉",
    playbook_collapse: "Collapse",
    playbook_expand: "Expand",
    playbook_filter_all: "All Dimensions",
    playbook_sources: "Sources",
    playbook_loading: "Generating your personalized playbook...",
    playbook_error: "Could not load playbook",
    playbook_empty: "No checkpoints found in framework",
    playbook_overall_progress: "Overall Progress",
    deps: {
      pageTitle: "Checkpoint Dependency Map",
      pageDesc: "Interactive visualization of checkpoint relationships across dimensions. Nodes are sized by maturity level, colored by dimension. Connected checkpoints share source frameworks.",
      graphTitle: "Force-Directed Graph",
      labels: "Labels",
      resetView: "Reset View",
      legendTitle: "Legend",
      selectedTitle: "Selected Checkpoint",
      clickHint: "Click a node to see checkpoint details.",
      dimension: "Dimension",
      level: "Level",
      category: "Category",
      sources: "Sources",
      dimStrategy: "Strategy",
      dimData: "Data & Infra",
      dimGovernance: "Governance",
      dimTechnology: "Tech & MLOps",
      dimTalent: "Talent",
      dimEthics: "Ethics & RAI",
      dimProcesses: "Processes"
    }
  },
  de: {
    nav: {
      dashboard: "Dashboard",
      explorer: "Framework Explorer",
      assessment: "Assessment",
      analyzer: "Dokumenten-Analyse",
      frameworkBuilder: "Framework Builder",
      research: "Research Agent",
      metaStrategy: "Meta-Strategie",
      euAiAct: "EU AI Act Engine",
      roadmap: "Roadmap",
      advisor: "AI Strategie-Berater",
      simulator: "Lücken-Simulator",
      sources: "Quellen & Attribution",
      dependencies: "Abhängigkeitskarte",
      evolution: "Evolutions-Agent",
      playbook: "KI-Playbook",
      backendOffline: "Backend Offline",
      backendOnline: "Backend Online"
    },
    dimensions: {
      strategy: "Strategie & Führung",
      data: "Daten & Infrastruktur",
      governance: "Governance & Compliance",
      technology: "Technologie & MLOps",
      talent: "Talent & Kultur",
      ethics: "Ethik & Verantwortungsbewusste KI",
      processes: "Prozesse & Skalierung"
    },
    levels: {
      exploring: "Erkundend",
      experimenting: "Experimentierend",
      operationalizing: "Operationalisierend",
      scaling: "Skalierend",
      transforming: "Transformierend"
    },
    dashboard: {
      title: "Dashboard",
      desc: "Ihre KI-Reife auf einen Blick — basierend auf Erkenntnissen aus NIST, EU AI Act, Google, Microsoft, OWASP und UNESCO Frameworks.",
      statScore: "Reifegrad-Score",
      statLevel: "Reifegrad-Stufe",
      statAnalyses: "Analysen",
      statSources: "Forschungsquellen",
      radarTitle: "📊 Reifegrad-Radar",
      breakdownTitle: "🎯 Score-Aufteilung",
      emptyStateLine1: "📋",
      emptyStateLine2: "Führen Sie ein Assessment durch oder analysieren Sie ein Dokument, um Ihre Scores zu sehen",
      startAssessment: "Assessment starten",
      quickActions: "⚡ Schnellzugriff",
      actionExplore: "Frameworks erkunden",
      actionAssess: "Assessment starten",
      actionAnalyze: "Dokument analysieren",
      actionBuilder: "Framework Builder",
      actionMeta: "Meta-Strategie",
      actionRoadmap: "Roadmap ansehen",
      trendTitle: "📈 Score-Trend",
      emptyTrend: "Führen Sie mindestens 2 Assessments durch, um Ihren Trend zu sehen.",
      historyTitle: "🕒 Assessment Historie & Berichte",
      btnReport: "📄 Consulting Report erstellen (PDF)",
      btnNewAssessment: "✅ Neues Assessment",
      emptyHistory: "Noch keine Assessments. Starten Sie mit dem Assessment oder laden Sie ein Dokument hoch.",
      deltaPlus: "📈 +{delta} Punkte seit letztem Mal",
      deltaMinus: "📉 {delta} Punkte seit letztem Mal",
      deltaSame: "→ Keine Änderung",
      onboardingTitle: "👋 Willkommen im AI Strategy Hub",
      onboardingDesc1: "Dies ist eine umfassende KI-Reifegrad-Assessment-Plattform, basierend auf 6 globalen Frameworks.",
      onboardingDesc2: "Sie können klein anfangen oder gleich tief eintauchen. Hier sind die besten Startpunkte:",
      onboardingOpt1Title: "1. Schnelles Assessment (Manuell)",
      onboardingOpt1Desc: "Beantworten Sie die Fragen der Checkliste selbst, um eine Basislinie zu erhalten.",
      onboardingOpt1Btn: "Assessment starten",
      onboardingOpt2Title: "2. Dokumenten-Analyse (Automatisch)",
      onboardingOpt2Desc: "Haben Sie bereits ein KI-Strategiedokument? Laden Sie es hoch und lassen Sie die KI es gegen die Checkpoints bewerten.",
      onboardingOpt2Btn: "Zum Document Analyzer",
      onboardingOpt3Title: "3. Chat mit dem Advisor (Interaktiv)",
      onboardingOpt3Desc: "Fragen Sie den KI-Strategie-Berater nach den Frameworks, Best Practices oder Ihren nächsten Schritten.",
      onboardingOpt3Btn: "Mit Advisor chatten",
      onboardingClose: "Loslegen",
      dimensionScore: "Dimension-Score"
    },
    assessment: {
      title: "Reifegrad-Assessment",
      desc: "Bewerten Sie die KI-Bereitschaft Ihrer Organisation über 7 Dimensionen. Jeder Checkpoint ist auf seine ursprüngliche Framework-Quelle zurückzuführen.",
      btnReset: "↺ Zurücksetzen",
      btnSubmit: "Assessment einreichen",
      resultTitle: "🎯 Assessment-Ergebnis",
      overallProgress: "Gesamtfortschritt",
      loading: "Assessment wird geladen…",
      aiSuggested: "🤖 {count} Checkpoints vorab ausgefüllt durch Document Analyzer (\"{source}\")",
      showEvidence: "Evidence Chain",
      hideEvidence: "Evidence Chain ausblenden",
      noEvidence: "Keine Belege aus dem Dokument extrahiert.",
      submitSuccess: "Assessment gespeichert! Score: {score}",
      submitError: "Assessment konnte nicht gespeichert werden. Läuft das Backend?",
      submitEmpty: "Bitte markieren Sie mindestens einen Checkpoint.",
      resetConfirm: "Sind Sie sicher, dass Sie Ihren Fortschritt zurücksetzen möchten?",
      resetSuccess: "Assessment zurückgesetzt",
      overallScore: "Gesamt-Score",
      viewRoadmap: "🗺️ Roadmap ansehen →",
      strategicGuidance: "🧭 Strategische Führung",
      covered: "✅ Abgedeckt",
      notCovered: "❌ Nicht abgedeckt",
      confidence: "Konfidenz: {pct}%",
      evidenceTitle: "Beleg:",
      recommendationTitle: "💡 Empfehlung:",
      sourceChunksTitle: "QUELL-ABSCHNITTE:"
    },
    explorer: {
      title: "Framework Explorer",
      desc: "Durchsuchen Sie die synthetisierte KI-Reifegradmatrix — jede Empfehlung ist auf ihre ursprüngliche Quelle von NIST, EU AI Act, Google, Microsoft, OWASP und UNESCO zurückzuführen.",
      filterTitle: "🔎 Nach Framework filtern",
      loading: "Meta-Modell wird geladen…",
      error: "Das Reifegradmodell konnte nicht geladen werden.",
      level: "Stufe",
      viewSourceDocs: "📄 Quelldokumente ansehen",
      allSources: "Alle Quellen",
      checkpointsAcross: "{total} Checkpoints über {dims} Dimensionen",
      matchFilter: "{visible} von {total} Checkpoints passen zu \"{filter}\"",
      evidenceRef: "Belege & Referenzen",
      openSource: "Quelle öffnen →",
      deepDive: "🤖 Deep Dive",
      deepDiveLoading: "⏳ Analysiere..."
    },
    analyzer: {
      title: "Document Analyzer",
      desc: "Laden Sie Ihre KI-Strategiedokumente hoch — die RAG-Pipeline gleicht sie mithilfe von Gemini mit allen Checkpoints des Reifegradmodells ab.",
      uploadTitle: "📤 Dokument hochladen",
      dropzoneText: "Ziehen Sie Ihr Dokument hierher",
      dropzoneHint: "Unterstützt PDF, DOCX, TXT, MD (max 20 MB)",
      orImport: "ODER AUS RESEARCH IMPORTIEREN",
      importLabel: "Einen vom Research Agent gefundenen Artikel analysieren",
      importLoading: "Quellen werden geladen...",
      btnImport: "Importieren & Analysieren",
      btnAnalyze: "🔍 Analysieren",
      statusAnalyzing: "Dokument wird analysiert…",
      progressSteps: {
        extracting_text: '📄 Text wird extrahiert…',
        chunking: '✂️ Semantische Aufteilung…',
        embedding: '🧠 Embeddings werden generiert…',
        evaluating: '🔍 Checkpoints werden bewertet…',
        scoring: '📊 Reifegrad wird berechnet…',
        generating_summary: '✨ Zusammenfassung wird erstellt…',
        storing_results: '💾 Ergebnisse werden gespeichert…',
        completed: '✅ Analyse abgeschlossen!'
      },
      resultTitle: "📊 Analyseergebnis",
      maturityScore: "Maturity Score",
      reportTitle: "📋 Detaillierter Bericht",
      strengths: "✅ Stärken",
      gaps: "⚠️ Lücken",
      recommendations: "💡 Empfehlungen",
      expandModelTitle: "🏗️ Erweitern Sie Ihr Meta-Modell",
      expandModelDesc: "Haben Sie ein Research-Dokument importiert? Verwenden Sie den Framework Builder, um neue Checkpoints zu extrahieren und Ihre Wissensbasis zu erweitern.",
      expandModelBtn: "Zum Framework Builder →",
      uploadError: "Upload fehlgeschlagen: {msg}",
      analysisError: "Analyse fehlgeschlagen: {msg}",
      analysisComplete: "Analyse abgeschlossen! Score: {score}",
      pastAnalyses: "📁 Letzte Analysen",
      noSources: "Noch keine Research-Quellen verfügbar",
      chooseSource: "-- Wählen Sie eine Quelle --",
      couldNotLoad: "Quellen konnten nicht geladen werden",
      fileTooLarge: "Datei zu groß (max 20 MB)",
      uploading: "Wird hochgeladen…",
      docUploaded: "Dokument hochgeladen: {name}",
      analyzingBtn: "Analysiere…",
      btnAnalyze: "🔍 Analysieren",
      analysisFailedLog: "Analyse fehlgeschlagen. Überprüfen Sie die Backend-Logs.",
      aiExecSummary: "🤖 KI-Zusammenfassung",
      noStrengths: "Keine Stärken identifiziert",
      noGaps: "Keine Lücken identifiziert",
      noRecommendations: "Keine Empfehlungen",
      cpMapping: "🗺️ Checkpoint-Mapping",
      cpEvaluated: "{count} Checkpoints bewertet",
      coveredCount: "Abgedeckt",
      gapsCount: "Lücken",
      coveragePct: "Abdeckung",
      dimsCount: "Dimensionen",
      filterAll: "Alle",
      filterCovered: "✅ Abgedeckt",
      filterGaps: "❌ Lücken",
      levelX: "Stufe {level}",
      confidenceLabel: "Konfidenz",
      depthLabels: {
        1: "Erwähnt",
        2: "Detailliert",
        3: "Implementiert"
      },
      noCheckpoints: "Keine Checkpoints passen zu diesem Filter.",
      noAnalyses: "Noch keine Analysen. Laden Sie ein Dokument hoch, um zu beginnen.",
      reportNotAvailable: "Bericht noch nicht verfügbar",
      startBackendAnalyses: "Starten Sie das Backend, um letzte Analysen zu sehen"
    },
    research: {
      title: "Research Agent",
      desc: "Entdecken Sie neue KI-Strategie-Frameworks, Vorschriften und Best Practices — automatisch bewertet nach Relevanz für Ihr Reifegradmodell.",
      runResearch: "🔬 Research starten",
      ingestTitle: "📥 Quellenaufnahme — Neue Quellen analysieren & integrieren",
      ingestDesc: "Geben Sie eine URL ein oder laden Sie ein Dokument hoch (PDF, TXT, DOCX). Gemini extrahiert die wichtigsten Argumente und ordnet sie den 7 Reifegraddimensionen zu. Sie können sie dann selektiv in Ihr Modell integrieren.",
      analyzeUrl: "URL analysieren",
      uploadFile: "Datei hochladen",
      analyzeBtn: "Analysieren",
      dragDropText: "Drag & Drop oder klicken zum Durchsuchen",
      dragDropHint: "PDF, TXT, DOCX, MD",
      apiKeysMissing: "API Keys nicht konfiguriert",
      apiKeysDesc: "Der Research Agent benötigt API-Keys. Erstellen Sie eine <code>.env</code> Datei im Projektroot:",
      apiKeysRestart: "Dann Backend neu starten.",
      searchConfig: "🔎 Research Konfiguration",
      customQuery: "Eigene Suchanfrage (optional)",
      customQueryPlaceholder: "z.B. AI governance best practices 2026 enterprise",
      focusDims: "Fokus-Dimensionen",
      maxSources: "Max. abzurufende Quellen:",
      statusHelp: "💡 Wählen Sie Dimensionen oder geben Sie eine Suchanfrage ein und klicken Sie auf \"Research starten\"",
      pdfOnly: "Nur nach PDFs suchen",
      langAll: "Alle Sprachen",
      langEn: "Englisch",
      langDe: "Deutsch",
      resultsCount: "{count} Ergebnisse",
      lastResults: "📊 Letzte Suchergebnisse",
      sourcesFeed: "📚 Quellen-Feed",
      catAll: "Alle Kategorien",
      catFramework: "Frameworks",
      catRegulation: "Vorschriften",
      catWhitepaper: "Whitepaper",
      catArticle: "Artikel",
      catReport: "Berichte",
      catTool: "Tools",
      dimAll: "Alle Dimensionen",
      searchingBtn: "⏳ Suche…",
      searchingStatus: "🔄 Research Agent sucht und bewertet Quellen…",
      researchFailed: "Research fehlgeschlagen",
      sourcesStored: "✅ {stored} neue Quellen gespeichert ({found} gefunden, {skipped} übersprungen)",
      noNewSources: "ℹ️ {found} gefunden, aber keine neuen Quellen zum Speichern",
      statFound: "Gefunden",
      statNew: "Neu",
      statStored: "Gespeichert",
      statSkipped: "Geringe Relevanz",
      sourceCountLabel: "{total} Quellen · {new} neu",
      noSourcesYet: "Noch keine Quellen. Starten Sie eine Research-Suche, um neue KI-Strategie-Inhalte zu entdecken.",
      markedAsRead: "Als gelesen markiert",
      couldNotUpdate: "Konnte nicht aktualisiert werden",
      startBackendSources: "Starten Sie das Backend, um Research-Quellen zu sehen",
      noSummary: "Keine Zusammenfassung verfügbar",
      relevantPct: "📊 {pct}% relevant",
      readBtn: "✓ Gelesen",
      extractBtn: "🏗️ Extrahieren",
      extractTooltip: "Neue Checkpoints für den Framework Builder extrahieren",
      extractingBtn: "⏳ ...",
      extractingMsg: "Extrahiere neue Checkpoints...",
      enterUrl: "Bitte eine URL eingeben",
      fetchingStatus: "🔄 Quelle wird mit Gemini abgerufen und analysiert...",
      extractedArgs: "✅ {count} Argumente aus \"{source}\" extrahiert",
      analysisFailed: "Analyse fehlgeschlagen: {msg}",
      selectFileFirst: "Bitte zuerst eine Datei auswählen",
      analyzingFile: "🔄 Analysiere \"{file}\" mit Gemini...",
      noArgsExtracted: "Keine Argumente extrahiert",
      extractedArgsTitle: "📋 Extrahierte Argumente — {source}",
      integrateAllBtn: "⚡ Alle Ausgewählten Integrieren",
      noArgsSelected: "Keine Argumente ausgewählt",
      integratingBtn: "⏳ Integriere...",
      integrationSuccess: "✅ {count} Argumente integriert → {target}",
      integrationStatus: "✅ {integrated} von {total} integriert in:",
      viewInExplorer: "Im Explorer ansehen →",
      integrationFailed: "Integration fehlgeschlagen: {msg}",
      noNovelInsights: "Keine neuen Erkenntnisse gefunden — das Meta-Modell deckt diese Quelle bereits ab.",
      novelCpProposals: "🏗️ {count} Neue Checkpoint-Vorschläge",
      integrateSelected: "➕ Ausgewählte integrieren ({count})",
      integrating: "Integriere...",
      cpsIntegratedSuccess: "{count} Checkpoints in das Meta-Modell integriert!",
      cpsIntegratedStatus: "✅ {count} Checkpoints erfolgreich integriert!"
    },
    advisor: {
      title: "🧠 KI-Strategie-Berater",
      desc: "Ihr persönlicher KI-Strategieberater — basierend auf Ihren Assessment-Daten, Framework-Wissen und Research-Quellen.",
      loadingContext: "Lade Kontext...",
      welcomeMsg: "<strong>Willkommen!</strong> Ich bin Ihr KI-Strategieberater, angetrieben von Gemini Pro und verankert im vollständigen Reifegradmodell.<br><br>Ich kenne Ihre aktuellen Assessment-Werte, Lücken, Stärken und Research-Quellen. Fragen Sie mich alles über Ihre KI-Reifegrad-Strategie.",
      sugg1: "🎯 Worauf sollte ich mich als Nächstes konzentrieren?",
      sugg1q: "Worauf sollte ich mich als Nächstes konzentrieren, um meinen KI-Reifegrad zu verbessern?",
      sugg2: "⚖️ EU AI Act Bereitschaft?",
      sugg2q: "Wie schneidet unsere Organisation im Vergleich zu den Anforderungen des EU AI Act ab?",
      sugg3: "📋 90-Tage-Aktionsplan",
      sugg3q: "Geben Sie mir einen 90-Tage-Aktionsplan, um von unserem aktuellen Level auf das nächste Reifegrad-Level zu kommen.",
      sugg4: "⚡ Quick Wins diese Woche?",
      sugg4q: "Was sind die Quick Wins, die wir diese Woche mit minimalem Aufwand erreichen können?",
      inputPlaceholder: "Fragen Sie nach Ihrer KI-Strategie, Reifegradlücken, Compliance oder nächsten Schritten...",
      sendBtn: "Senden ↑",
      contextDims: "📊 {dims} Dimensionen, {cps} Checkpoints",
      contextAssLoaded: "✅ Assessment geladen",
      contextResLoaded: "🔬 Research geladen",
      backendOffline: "⚠️ Backend offline",
      sending: "...",
      failedResponse: "Antwort fehlgeschlagen: {msg}",
      advisorError: "Beraterfehler: {msg}",
      unknownError: "Unbekannter Fehler",
      youAvatar: "Sie"
    },
    roadmap: {
      title: "Umsetzbare Roadmap",
      desc: "Sehen Sie, was erforderlich ist, um Ihre nächste Reifegradstufe zu erreichen — priorisiert nach Wirkung und Aufwand.",
      exportMd: "📄 MD Export",
      exportPdf: "📕 PDF Export",
      targetConfig: "🎯 Zielkonfiguration",
      targetLevel: "Ziel-Reifegrad",
      generateBtn: "Roadmap erstellen",
      generatingBtn: "⏳ Erstelle…",
      currentScore: "Aktueller Score",
      currentLevel: "Aktuelles Level",
      openGaps: "Offene Lücken",
      quickWins: "Quick Wins",
      qwTitle: "⚡ Quick Wins — Geringer Aufwand, hohe Wirkung",
      gapsTitle: "📊 Lücken nach Dimension",
      fullRoadmapTitle: "📋 Vollständige Roadmap",
      roadmapItemsCount: "{count} Elemente · Ziel: Level {level} {levelName}",
      emptyStateMsg: "Wählen Sie ein Ziel-Level aus und klicken Sie auf \"Roadmap erstellen\", um Ihre priorisierten Maßnahmen zu sehen.",
      effortLow: "Gering",
      effortMed: "Mittel",
      effortHigh: "Hoch",
      effortSuffix: "{effort}er Aufwand",
      levelPlus: "Level {level}+",
      qwBadge: "⚡ Quick Win",
      roadmapGenerated: "Roadmap erstellt: {count} Lücken gefunden",
      roadmapFailed: "Konnte Roadmap nicht erstellen. Läuft das Backend?",
      exportSuccess: "{format} Bericht heruntergeladen!",
      exportFailed: "Export fehlgeschlagen: {msg}"
    },
    simulator: {
      title: "🎮 Lücken-Simulator",
      desc: "Schalten Sie Checkpoints um, um Score-Änderungen zu simulieren — entdecken Sie, welche Aktionen den größten Einfluss auf Ihren Reifegrad haben.",
      resetBtn: "↩️ Zurücksetzen",
      currentState: "📊 Aktueller Zustand",
      simState: "🎯 Simulierter Zustand",
      impactTitle: "⚡ Checkpoints mit höchster Auswirkung",
      impactSub: "Nach Score-Einfluss sortiert — zum Simulieren umschalten",
      noChange: "Keine Änderung",
      ptsIncrease: "📈 +{pts} Punkte",
      ptsDecrease: "📉 {pts} Punkte",
      allFulfilled: "🎉 Alle Checkpoints erfüllt!",
      dimImpactTitle: "📊 Dimensionseinfluss",
      failedLoad: "Konnte Framework-Modell nicht laden."
    },
    meta: {
      title: "Meta-Strategie",
      desc: "Strategische Synthese aller 6 globalen KI-Frameworks — umsetzbare Do's & Don'ts, framework-spezifische Erkenntnisse und reifegradbasierte Empfehlungen für jede Dimension.",
      overviewTitle: "🧭 Strategischer Überblick",
      generateAiRec: "🤖 KI-Empfehlungen generieren",
      metaSummary: "Dieses Meta-Modell synthetisiert <strong>6 Frameworks</strong> (NIST, EU AI Act, Google, Microsoft, OWASP, UNESCO) in <strong>7 Dimensionen</strong> mit <strong>69 Checkpoints</strong>. Diese Seite destilliert die strategische Essenz — worin sich die weltweit führenden Frameworks einig sind, wo sie sich unterscheiden und was Sie auf jeder Reifegradstufe tun (und nicht tun) sollten.",
      globalFws: "Globale Frameworks",
      dimensions: "Dimensionen",
      checkpoints: "Checkpoints",
      aiRecTitle: "🤖 Personalisierte KI-Empfehlungen",
      loadingGuidance: "Lade strategische Empfehlungen...",
      checkpointsLabel: "Checkpoints",
      weightLabel: "Gewichtung:",
      dosTitle: "✅ Do's",
      recsCount: "{count} Empfehlungen",
      achieved: "✓ erreicht",
      deepDiveBtn: "🤖 Deep Dive",
      dontsTitle: "❌ Don'ts",
      warningsCount: "{count} Warnungen",
      whatFrameworksSay: "🔍 Was die Frameworks sagen",
      analyzing: "⏳ Analysiere...",
      failedDeepDive: "Deep Dive konnte nicht generiert werden: {msg}",
      modelNotLoaded: "Modell noch nicht geladen",
      assessmentRequired: "Führen Sie zuerst das Assessment durch, um personalisierte Empfehlungen zu erhalten",
      generatingBtn: "⏳ Generiere...",
      geminiAnalyzing: "Gemini analysiert Ihr Reifegrad-Profil...",
      generationFailed: "Konnte Empfehlungen nicht generieren: {msg}",
      noRecommendations: "Keine Empfehlungen generiert",
      executiveSummary: "Zusammenfassung",
      expectedImpact: "📈 Erwarteter Einfluss:"
    },
    report: {
      noAssessment: "Keine Assessment-Daten verfügbar. Bitte führen Sie zuerst das Assessment durch.",
      goToAssessment: "Zum Assessment",
      generatingAi: "Generiere Executive Summary via KI (Gemini 3.1 Pro)...",
      checkpointsUnmet: "{dim}: {unmet} von {total} Checkpoints nicht erfüllt",
      checkpointsMet: "{dim}: {met} von {total} Checkpoints erfüllt ({score}%)",
      briefingFailed: "Briefing-Generierung fehlgeschlagen",
      headerSub: "AI Strategy Hub — Executive Report",
      headerTitle: "AI Maturity Executive Briefing",
      generatedOn: "Generiert am {date}",
      scoreLabel: "Score / 100 · Level {level}",
      footer: "AI Strategy Hub • Basierend auf NIST, EU AI Act & 4 weiteren • Gemini 3.1 Pro",
      downloadPdf: "📥 PDF herunterladen",
      generatingPdf: "⏳ Generiere PDF...",
      printBtn: "🖨️ Drucken",
      backBtn: "🔙 Zurück",
      failedLoad: "Report konnte nicht geladen werden: {msg}",
      pdfFailed: "PDF-Generierung fehlgeschlagen: {msg}"
    },
    eu: {
      title: "⚖️ EU AI Act Compliance Engine",
      desc: "Automatisierte Prüfung Ihres KI-Portfolios gemäß dem Europäischen Gesetz über künstliche Intelligenz (Inkrafttreten ab August 2026).",
      initializing: "Initialisiere Compliance-Prüfung...",
      failedLoad: "Konnte Framework-Modell nicht laden. Backend könnte offline sein.",
      criticalRisk: "Kritisches Risiko",
      lowRisk: "Geringes Risiko",
      mediumRisk: "Mittleres Risiko",
      prioritizedGaps: "🚨 Priorisierte Lücken ({count})",
      generalObligations: "Allgemeine Pflichten",
      violation: "Verstoß:",
      fixViaAi: "🤖 Beheben durch KI-Deep-Dive",
      deepDiveContext: "EU AI Act Compliance Lücken-Mapping zu: {refs}",
      fullyCompliantTitle: "Vollständig konform",
      fullyCompliantDesc: "Sie haben alle Checkpoints erfüllt, die mit dem EU AI Act verknüpft sind.",
      readinessScore: "Readiness Score",
      regExposure: "Regulatorisches Risiko",
      openGaps: "Offene Compliance-Lücken",
      penaltiesTitle: "⚠️ Strafen bei Nichteinhaltung",
      penaltiesDesc: "Nach dem EU AI Act können administrative Geldbußen bis zu <strong>35.000.000 €</strong> oder <strong>7 % des weltweiten Jahresumsatzes</strong> des vorangegangenen Geschäftsjahres (je nachdem, welcher Wert höher ist) bei Nichteinhaltung verbotener KI-Praktiken verhängt werden. Bei Pflichten im Zusammenhang mit Hochrisiko-KI-Systemen können Bußgelder bis zu <strong>15.000.000 €</strong> oder <strong>3 %</strong> des weltweiten Umsatzes betragen.",
      completeAssessmentPrompt: "Führen Sie das Assessment durch, um Ihre spezifischen Compliance-Lücken zu analysieren.",
      startAssessmentBtn: "Assessment starten",
      genRoadmapTitle: "Compliance-Roadmap generieren",
      genRoadmapDesc: "Verwandeln Sie Ihre {count} Compliance-Lücken in einen priorisierten, umsetzbaren Behebungsplan mit Aufwandsschätzungen und Quick Wins.",
      openRoadmapBtn: "🗺️ Roadmap öffnen →",
      generatingFix: "⏳ Generiere Lösung...",
      failedDeepDive: "Deep Dive konnte nicht generiert werden: {msg}"
    },
    builder: {
      title: "🏗️ Framework Builder",
      desc: "Erweitern Sie das KI-Strategie-Meta-Modell, indem Sie neue strategische Richtlinien aus Forschungsdokumenten extrahieren. Automatisch angereichert mit verknüpften Forschungsnachweisen.",
      coverageTitle: "📊 Forschungsabdeckungsanalyse",
      coverageDesc: "Zeigt, welche Reifegraddimensionen die meisten/wenigsten Forschungsnachweise haben. Fokussieren Sie Ihre Forschung auf weniger abgedeckte Bereiche.",
      step1Title: "1. Forschungsquelle auswählen",
      step1Desc: "Wählen Sie ein Dokument, das zuvor über den Research Agent importiert wurde (.txt)",
      loadingSources: "Lade Quellen...",
      extractBtn: "Neue Erkenntnisse extrahieren",
      extractingMsg: "Analysiere Framework und destilliere strategische Regeln... (Dies kann bis zu 20 Sekunden dauern)",
      step2Title: "2. Überprüfen & Integrieren",
      integrateBtn: "➕ Ausgewählte integrieren ({count})",
      step2Desc: "Die KI hat die folgenden strategischen Checkpoints identifiziert, die in Ihrem Meta-Modell fehlen. Wählen Sie diejenigen aus, die Sie Ihrem Framework hinzufügen möchten.",
      activityTitle: "📜 Integrationsaktivitätsprotokoll",
      noCoverageData: "Keine Abdeckungsdaten verfügbar",
      checkpointsStat: "Checkpoints",
      sourcesStat: "Forschungsquellen",
      builderAdditionsStat: "Builder-Ergänzungen",
      checkpointsLabel: "{count} Checkpoints",
      sourcesLabel: "{count} Quellen",
      builtLabel: "+{count} erstellt",
      researchBtn: "🔬 Forschen",
      failedLoadCoverage: "Abdeckungsdaten konnten nicht geladen werden. Backend starten.",
      noActivity: "Noch keine Aktivität. Führen Sie Forschung durch oder integrieren Sie Checkpoints, um den Lebenszyklus hier zu sehen.",
      actionResearch: "Forschung abgeschlossen",
      actionExtracted: "Für Framework extrahiert",
      actionIntegrated: "Checkpoint integriert",
      sourcesStored: "{count} Quellen gespeichert",
      proposalsCount: "({count} Vorschläge)",
      failedLoadActivity: "Aktivitätsdaten konnten nicht geladen werden.",
      noSourcesMsg: "Keine importierte Forschung gefunden. Gehen Sie zum Dokument-Analysator und importieren Sie zuerst eine.",
      chooseSource: "-- Forschungsdokument auswählen --",
      failedLoadSources: "Quellen konnten nicht geladen werden",
      extractionFailed: "Extraktion fehlgeschlagen: {msg}",
      noNovelInsights: "Keine neuen Erkenntnisse gefunden. Das Meta-Modell deckt dieses Dokument bereits umfassend ab.",
      linkedEvidence: "📚 Verknüpfte Forschungsnachweise",
      targetDimension: "Zieldimension:",
      rationale: "Begründung:",
      minLevel: "Min. Level:",
      integrating: "Integriere...",
      integrationSuccess: "Erfolgreich {count} Checkpoints zum Meta-Modell hinzugefügt!",
      integrationFailed: "Integration fehlgeschlagen: {msg}"
    },
    sources: {
      title: "📚 Quellen & Referenzen",
      desc: "Jeder Checkpoint in dieser Plattform ist auf sein ursprüngliches Framework rückverfolgbar. Diese Seite bietet volle Transparenz über die 6 weltweit anerkannten Standards, die das Fundament des Reifegradmodells bilden.",
      coverageTitle: "🔬 Framework-Abdeckung",
      checkpointsTotal: "{count} Checkpoints insgesamt",
      checkpointsPct: "{count} Checkpoints ({pct}%)",
      version: "📄 <strong>Version:</strong> {version}",
      published: "📅 <strong>Veröffentlicht:</strong> {date}",
      officialSource: "🔗 Offizielle Quelle ↗",
      disclaimerTitle: "⚖️ Haftungsausschluss & Fair Use",
      disclaimer1: "Der AI Strategy Hub synthetisiert öffentlich zugängliche Frameworks und Richtlinien in ein einheitliches Bewertungsmodell. Alle referenzierten Frameworks bleiben das geistige Eigentum der jeweiligen Organisationen. Dieses Tool ersetzt <strong>keine</strong> professionelle Rechts-, Compliance- oder Prüfungsberatung.",
      disclaimer2: "<strong>Für die Einhaltung des EU AI Act:</strong> Konsultieren Sie einen qualifizierten Rechtsbeistand für verbindliche regulatorische Bewertungen. Die Compliance-Readiness-Scores in diesem Tool sind indikativ und basieren auf Selbsteinschätzung — sie sind keine rechtsverbindlichen Konformitätsbewertungen.",
      disclaimer3: "Die Framework-Daten wurden zuletzt im <strong>April 2026</strong> verifiziert. Quell-Frameworks könnten seitdem aktualisiert worden sein. Beziehen Sie sich immer auf die oben verlinkten offiziellen Quellen für die neuesten Versionen."
    },
    evolution: {
      title: "Evolutions-Dashboard",
      subtitle: "Autonome Framework-Evolution — überwachen, genehmigen und verwalten Sie, wie sich Ihr KI-Reifegradmodell weiterentwickelt und verfeinert.",
      status_loading: "Status wird geladen…",
      status_active: "Evolutions-Agent Aktiv",
      status_paused: "Evolutions-Agent Pausiert",
      status_running: "Evolution läuft…",
      status_offline: "Agent Offline",
      next_run: "Nächster geplanter Lauf",
      last_run: "Letzter Lauf",
      run_now: "Jetzt ausführen",
      configure: "Konfigurieren",
      running: "Läuft…",
      total_runs: "Gesamte Läufe",
      integrated: "Integrierte Checkpoints",
      redundancies_resolved: "Behobene Redundanzen",
      avg_quality: "Ø Qualitäts-Score",
      timeline_title: "Evolutions-Timeline",
      timeline_empty: "Noch keine Evolutionsläufe. Starten Sie Ihren ersten Lauf.",
      growth_title: "Framework-Wachstum",
      no_growth_data: "Noch nicht genügend Daten für das Wachstumsdiagramm.",
      proposals_title: "Ausstehende Vorschläge",
      proposals_empty: "Keine ausstehenden Vorschläge. Alles erledigt!",
      approve: "Genehmigen",
      reject: "Ablehnen",
      approve_all: "Alle genehmigen",
      quality: "Qualität",
      impact: "Auswirkung",
      novelty: "Neuartigkeit",
      proposal_approved: "Vorschlag genehmigt!",
      proposal_rejected: "Vorschlag abgelehnt.",
      bulk_approved: "Alle Vorschläge genehmigt!",
      redundancy_title: "Redundanz-Scanner",
      scan_redundancies: "Nach Redundanzen suchen",
      scanning: "Scanne…",
      redundancy_empty: "Klicken Sie auf \"Scannen\", um ähnliche oder überlappende Checkpoints zu erkennen.",
      no_redundancies: "Keine Redundanzen gefunden — Ihr Framework ist sauber!",
      similarity: "ähnlich",
      merge: "Zusammenführen",
      merge_success: "Checkpoints erfolgreich zusammengeführt!",
      snapshots_title: "Snapshots & Rollback",
      snapshots_empty: "Noch keine Snapshots verfügbar.",
      date: "Datum",
      rollback: "Zurücksetzen",
      rollback_confirm: "Sind Sie sicher, dass Sie auf {version} zurücksetzen möchten? Dies setzt das Framework auf diesen Zustand zurück.",
      rollback_success: "Erfolgreich auf {version} zurückgesetzt!",
      trigger_success: "Evolutionslauf erfolgreich gestartet! Dauert 30-60 Minuten.",
      trigger_error: "Evolution konnte nicht gestartet werden",
      already_running: "Evolutionszyklus laeuft bereits — schauen Sie in einigen Minuten nochmal.",
      run_complete: "Evolutionszyklus abgeschlossen! Daten werden aktualisiert...",
      backend_offline: "Starten Sie das Backend, um Evolutionsdaten zu sehen.",
      no_logs: "Keine Log-Einträge verfügbar.",
      imminent: "Unmittelbar bevorstehend",
      config_schedule: "Zeitplan",
      config_auto_approve: "Auto-Genehmigung",
      config_auto_approve_label: "Hochwertige Vorschläge automatisch genehmigen",
      config_quality_threshold: "Qualitätsschwelle (%)",
      config_save: "Konfiguration speichern",
      config_saved: "Konfiguration gespeichert!"
    },
    playbook_title: "KI-Strategie Playbook",
    playbook_subtitle: "Ihre Schritt-für-Schritt-Anleitung von Null zur KI-First-Organisation",
    playbook_based_on: "Basierend auf {count} Checkpoints · Modell {version}",
    playbook_phase: "Phase",
    playbook_steps: "Schritte",
    playbook_duration: "Geschätzte Dauer",
    playbook_key_focus: "Schwerpunkt",
    playbook_effort_low: "Geringer Aufwand",
    playbook_effort_medium: "Mittlerer Aufwand",
    playbook_effort_high: "Hoher Aufwand",
    playbook_priority_critical: "Kritisch",
    playbook_priority_high: "Hohe Priorität",
    playbook_priority_medium: "Mittel",
    playbook_priority_standard: "Standard",
    playbook_progress: "Fortschritt",
    playbook_completed: "{count} von {total} abgeschlossen",
    playbook_all_completed: "Phase abgeschlossen! 🎉",
    playbook_collapse: "Einklappen",
    playbook_expand: "Ausklappen",
    playbook_filter_all: "Alle Dimensionen",
    playbook_sources: "Quellen",
    playbook_loading: "Ihr personalisiertes Playbook wird erstellt...",
    playbook_error: "Playbook konnte nicht geladen werden",
    playbook_empty: "Keine Checkpoints im Framework gefunden",
    playbook_overall_progress: "Gesamtfortschritt",
    deps: {
      pageTitle: "Checkpoint Abhängigkeitskarte",
      pageDesc: "Interaktive Visualisierung der Checkpoint-Beziehungen über Dimensionen hinweg. Knoten werden nach Reifegrad skaliert und nach Dimension eingefärbt. Verbundene Checkpoints teilen Quell-Frameworks.",
      graphTitle: "Kräftebasierter Graph",
      labels: "Beschriftungen",
      resetView: "Ansicht zurücksetzen",
      legendTitle: "Legende",
      selectedTitle: "Ausgewählter Checkpoint",
      clickHint: "Klicken Sie auf einen Knoten, um Checkpoint-Details zu sehen.",
      dimension: "Dimension",
      level: "Stufe",
      category: "Kategorie",
      sources: "Quellen",
      dimStrategy: "Strategie",
      dimData: "Daten & Infra",
      dimGovernance: "Governance",
      dimTechnology: "Tech & MLOps",
      dimTalent: "Talent",
      dimEthics: "Ethik & RAI",
      dimProcesses: "Prozesse"
    }
  }
};

export function t(key) {
  const parts = key.split('.');
  let obj = dict[currentLang];
  
  for (const part of parts) {
    if (!obj || obj[part] === undefined) {
      // Fallback to EN
      let fallbackObj = dict['en'];
      for (const p of parts) {
        if (!fallbackObj || fallbackObj[p] === undefined) return key;
        fallbackObj = fallbackObj[p];
      }
      return fallbackObj;
    }
    obj = obj[part];
  }
  return obj;
}

export function getLang() {
  return currentLang;
}

export function setLang(lang) {
  currentLang = lang;
  localStorage.setItem('ai_strategy_hub_lang', lang);
  updateStaticDOM();
}

export function toggleLang() {
  setLang(currentLang === 'en' ? 'de' : 'en');
}

// Function to update any static DOM elements with data-i18n attribute
export function updateStaticDOM() {
  document.querySelectorAll('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n');
    el.innerText = t(key);
  });
  
  // Update toggle button UI
  const langIcon = document.getElementById('lang-icon');
  const langLabel = document.getElementById('lang-label');
  if (langIcon && langLabel) {
    langIcon.innerText = currentLang === 'en' ? '🇬🇧' : '🇩🇪';
    langLabel.innerText = currentLang === 'en' ? 'EN' : 'DE';
  }
}
